import { Package, PackageOnCard } from './package.type';

export function convertPackageData(originalData: Package): PackageOnCard {
  const features = [];
  const packageName = originalData.name?.trim() || '';
  const normalizedName = packageName.toLowerCase();
  const isBasic = normalizedName === 'basic';
  const isBasicPlus = normalizedName === 'basic+' || normalizedName === 'basic +';
  const isPremium = normalizedName === 'premium';

  // Basic: Show all features that Basic has (order matches admin package-setting)
  if (isBasic) {
    if (originalData.listedUntilSold) {
      features.push({
        name: 'Listed Until Sold',
        included: true,
      });
    }

    if (originalData.allFieldsForListing) {
      features.push({
        name: 'All-inclusive fields for listing',
        included: true,
      });
    }

    if (originalData.inventoryAnalytics) {
      features.push({
        name: 'Inventory analytics',
        included: true,
      });
    }

    if (originalData.privateMessagesWithBuyers) {
      features.push({
        name: 'Private message with buyers',
        included: true,
      });
    }

    if (originalData.photosPerListing) {
      features.push({
        name: 'Photos per listing',
        value: `${originalData.photosPerListing} Photos`,
      });
    }
  }

  // Basic+: Only show features that are NOT in Basic (order matches admin package-setting)
  if (isBasicPlus) {
    // Photos per listing - different count, so show it
    if (originalData.photosPerListing) {
      features.push({
        name: 'Photos per listing',
        value: `${originalData.photosPerListing} Photos`,
      });
    }

    // Embed Videos - not in Basic
    if (originalData.embedVideosPerListing > 0) {
      features.push({
        name: 'Embed Videos per listing',
        value: `${originalData.embedVideosPerListing} Videos`,
        description: '(Youtube, Vimeo)',
      });
    }

    // Featured in Search - not in Basic
    if (originalData.featuredInSearch) {
      features.push({
        name: 'Featured in Search',
        included: true,
      });
    }

    // Top featured in Search - not in Basic (but show if exists)
    if (originalData.topFeaturedInSearch) {
      features.push({
        name: 'Top featured in Search',
        included: true,
      });
    }

    // Featured in Homepage - not in Basic (but show if exists)
    if (originalData.featuredInHomepage) {
      features.push({
        name: 'Featured in Homepage',
        included: true,
      });
    }

    // Recommend to the top - not in Basic
    if (originalData.recommendToTheTop) {
      features.push({
        name: 'Recommend to the top',
        included: true,
        description: '("Similar Vehicles" search)',
      });
    }

    // Add "All benefits in Basic"
    features.push({
      name: 'All benefits in Basic',
      included: true,
    });
  }

  // Premium: Only show features that are NOT in Basic+ (order matches admin package-setting)
  if (isPremium) {
    // Photos per listing - different count, so show it
    if (originalData.photosPerListing) {
      features.push({
        name: 'Photos per listing',
        value: `${originalData.photosPerListing} Photos`,
      });
    }

    // Embed Videos - different count, so show it
    if (originalData.embedVideosPerListing > 0) {
      features.push({
        name: 'Embed Videos per listing',
        value: `${originalData.embedVideosPerListing} Videos`,
        description: '(Youtube, Vimeo)',
      });
    }

    // Featured in Search - show if exists (may be in Basic+ but show for consistency)
    if (originalData.featuredInSearch) {
      features.push({
        name: 'Featured in Search',
        included: true,
      });
    }

    // Top featured in Search - not in Basic+ (Basic+ only has featuredInSearch)
    if (originalData.topFeaturedInSearch) {
      features.push({
        name: 'Top featured in Search',
        included: true,
      });
    }

    // Featured in Homepage - not in Basic+
    if (originalData.featuredInHomepage) {
      features.push({
        name: 'Featured in Homepage',
        included: true,
      });
    }

    // Recommend to the top - show if exists (may be in Basic+ but show for consistency)
    if (originalData.recommendToTheTop) {
      features.push({
        name: 'Recommend to the top',
        included: true,
        description: '("Similar Vehicles" search)',
      });
    }

    // Premium Spotlight badge - not in Basic+
    if (originalData.premiumSpotlightBadge) {
      features.push({
        name: '"Premium Spotlight" badge',
        included: true,
      });
    }

    // Featured on Social Media - not in Basic+
    if (originalData.featuredOnSocialMedia) {
      features.push({
        name: 'Featured on Social Media',
        included: true,
        description: '(Instagram & Facebook)',
      });
    }

    // Inclusion to Newsletter - not in Basic+
    if (originalData.inclusionToNewsletter) {
      features.push({
        name: 'Inclusion to Newsletter',
        included: true,
        description: '(Sent to our active buyers database)',
      });
    }

    // Concierge support - not in Basic+
    if (originalData.conciergeSupport) {
      features.push({
        name: 'Concierge support',
        included: true,
        description: '(Free optional professional reviews)',
      });
    }

    // Add "All benefits in Basic+"
    features.push({
      name: 'All benefits in Basic+',
      included: true,
    });
  }

  return {
    id: originalData.id,
    name: originalData.name,
    price: originalData.price,
    recommendToTheTop: originalData.recommendToTheTop,
    features,
  };
}
